/**
 *
 * "Form for entering and CRUDing a Kit it Edit deck page.
 *
 *
 * @file   KitItemForm.js
 * @author Lateral
 * @since  2023
 */
import {
  Autocomplete,
  Button,
  createFilterOptions,
  Grid,
  OutlinedInput,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { KitsItems, KitSizeItems, PanelsItems } from 'models'
import { useForm, Controller } from 'react-hook-form'
import React, { useEffect, useMemo, useState } from 'react'
import { getColorsForKits } from 'common/colours'
import BigNumber from 'bignumber.js'
import { FormDialog } from 'components'
import RemoveForm from './RemoveForm'

import { useTheme } from '@mui/material/styles'
import { useGetMaterials } from 'hooks/database/useGetMaterials'
import { groupBy } from 'common/arrays'
import { DeckAccordion } from 'pages/deck/common/DeckAccordion'
import { useBackgroundProcess } from 'context/BackgroundProcessContext'
import { useGetCustomerMeta } from 'hooks/database/useGetCustomerMeta'
import { useCurrentUser } from 'hooks'
import { useSnackbar } from 'notistack'
import { LoadingButton } from '@mui/lab'

const inputStyling = {
  width: '100%',
  margin: '0.5em 0 0.5em 0'
}

const filterOptions = createFilterOptions({
  matchFrom: 'any',
  limit: 500
})

const defaultString = ''

const sideLinerName = 'sideliner'
const panelName = 'panel'
const otherName = 'other'

function KitItemForm({ deckRevision, setDeckRevision, setMaterialSelected, materialSelected, data, onChange }) {
  /**
   * Generates a form to CRUD kit items in deck revision
   *
   * @function
   *
   * @param {object} deckRevision - Object of deckrevion details
   * @param {} setDeckRevision - Funtion to set deckrevision object
   * @param {object} data - Kits details
   * @param {string} data.vNumberName - Material Description
   * @param {string} data.vNumberFormName - Material Number
   * @param {string} data.descriptionName - Kit Description
   * @param {string} data.partNumber - Material Description
   * @param {string} data.vNumberName - Kit Part Number
   * @param {string} data.CustomerStockCode - Code in customer stock
   * @param {string} data.Weight - Unit Weight (kg)
   * @param {string} data.Colour - Material Description
   * @param {string} data.OpenArea - Open area of kit
   * @param {string} data.WorkingDepth - Working depth of kit before failing
   * @param {string} data.FailureWorkingDepth - Working depth of kit of failing
   * @param {string} data.ApertureWidth - Aperature width
   * @param {string} data.FailureApertureWidth - Minimum failing aperature
   * @param {string} data.size.Height - Height of the kit
   * @param {string} data.size.Width - Width of the kit
   *
   * @returns {object} - Form to create/Update kit
   */
  const vNumberName = 'Material Description'
  const vNumberFormName = 'Material Number'
  const descriptionName = 'Description'
  const partNumberName = 'OEM Number'
  const siteStockNumber = 'Site Stock Number'
  const quantityName = 'Quantity'
  const weightName = 'Unit Weight (kg)'
  const colourName = 'Colour'
  const openAreaName = 'Open Area'
  const depthName = 'Working Depth'
  const failureDepthName = 'Failure Working Depth'
  const apertureName = 'Aperture Width'
  const failureApertureName = 'Failure Aperture Width'
  const heightName = 'Height'
  const widthName = 'Width'
  const commentsName = 'Comments'
  const { materials } = useGetMaterials()

  const defaultValues = {
    [vNumberName]: data?.MaterialNumber ?? defaultString,
    [vNumberFormName]: { Number: data?.MaterialNumber ?? defaultString },
    [descriptionName]: data?.MaterialDescription ?? defaultString,
    [partNumberName]: data?.PartNumber ?? defaultString,
    [siteStockNumber]: data?.CustomerStockCode ?? defaultString,
    [quantityName]: data?.Quantity ?? 0,
    [weightName]: data?.Weight ?? defaultString,
    [colourName]: data?.Colour ?? defaultString,
    [openAreaName]: data?.OpenArea ?? defaultString,
    [depthName]: data?.WorkingDepth ?? defaultString,
    [failureDepthName]: data?.FailureWorkingDepth ?? defaultString,
    [apertureName]: data?.ApertureWidth ?? defaultString,
    [failureApertureName]: data?.FailureApertureWidth ?? defaultString,
    [heightName]: data?.Size?.Height ?? '1',
    [widthName]: data?.Size?.Width ?? '1',
    [commentsName]: materials.find((mat) => mat.MaterialNumber === data?.MaterialNumber)?.Comments ?? ''
  }

  const {
    getValues,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    register,
    reset,
    control,
    watch,
    formState: { errors }
  } = useForm({ defaultValues })

  const { addProcess } = useBackgroundProcess()

  const [isSandvikMaterial, setIsSandvikMaterial] = useState(true)
  const [removeModalState, setRemoveModalState] = useState({ isOpen: false })
  const isOther =
    !data ||
    (!deckRevision.Panels.some((p) => p.MaterialNumber === data.MaterialNumber) &&
      !deckRevision.SideLiners.some((s) => s.MaterialNumber === data.MaterialNumber))

  const { enqueueSnackbar } = useSnackbar()

  const theme = useTheme()
  const { currentUser } = useCurrentUser()

  const { metadata } = useGetCustomerMeta(currentUser.customerIds)

  useEffect(() => {
    reset({
      [failureDepthName]: data?.FailureWorkingDepth,
      [vNumberName]: data?.MaterialNumber ?? defaultString,
      [vNumberFormName]: { Number: data?.MaterialNumber ?? defaultString },
      [descriptionName]: data?.MaterialDescription ?? defaultString,
      [partNumberName]: data?.PartNumber ?? defaultString,
      [siteStockNumber]: data?.CustomerStockCode ?? defaultString,
      [quantityName]: data?.Quantity ?? 0,
      [weightName]: data?.Weight ?? defaultString,
      [colourName]: data?.Colour ?? defaultString,
      [openAreaName]: data?.OpenArea ?? defaultString,
      [depthName]: data?.WorkingDepth ?? defaultString,
      [apertureName]: data?.ApertureWidth ?? defaultString,
      [failureApertureName]: data?.FailureApertureWidth ?? defaultString,
      [heightName]: data?.Size?.Height ?? '1',
      [widthName]: data?.Size?.Width ?? '1',
      [commentsName]: data?.Comments ?? ''
    })
  }, [data])

  const watchVNumber = watch(vNumberName, defaultString)

  useEffect(() => {
    setIsSandvikMaterial(!!materials.find(({ MaterialNumber }) => MaterialNumber === materialSelected))
  }, [materialSelected])

  useEffect(() => {
    if (data?.MaterialNumber) {
      setValue(
        failureDepthName,
        materials.find(({ MaterialNumber }) => data?.MaterialNumber === MaterialNumber)?.SafetyDepth
      )
    }
    setValue(commentsName, data?.Comments ?? '')
  }, [materials])

  const sideLiners = useMemo(() => {
    return groupBy('MaterialNumber')(deckRevision.SideLiners)
  }, [deckRevision.SideLiners, deckRevision.SideLiners?.length])

  const panels = useMemo(() => {
    return groupBy('MaterialNumber')(deckRevision.Panels)
  }, [deckRevision.Panels, deckRevision.Panels?.length])

  const kits = useMemo(() => {
    return [...(deckRevision.Kits ?? [])].sort((a, b) => a.MaterialNumber.localeCompare(b.MaterialNumber))
  }, [deckRevision.Kits, deckRevision.Kits?.length])

  const [kitData, setKitData] = useState({
    sideLinerKits: [],
    panelKits: [],
    otherKits: []
  })

  const sideLinersMaterials = useMemo(() => {
    return kitData.sideLinerKits.map((s) => {
      return s.MaterialNumber
    })
  }, [kitData.sideLinerKits])

  const panelMaterials = useMemo(() => {
    return kitData.panelKits.map((s) => {
      return s.MaterialNumber
    })
  }, [kitData.panelKits])

  const otherMaterials = useMemo(() => {
    return kitData.otherKits.map((s) => {
      return s.MaterialNumber
    })
  }, [kitData.panelKits])

  function panelTypeCheck(material) {
    const panelKeys = Object.keys(panels)
    const sideLinerKeys = Object.keys(sideLiners)

    if (panelKeys.includes(material)) {
      return panelName
    }

    if (sideLinerKeys.includes(material)) {
      return sideLinerName
    }

    return otherName
  }

  useEffect(() => {
    const sideLinerKits = kits.filter((k) => panelTypeCheck(k.MaterialNumber) === sideLinerName)
    const panelKits = kits.filter((k) => panelTypeCheck(k.MaterialNumber) === panelName)
    const otherKits = kits.filter((k) => panelTypeCheck(k.MaterialNumber) === otherName)

    setKitData({
      sideLinerKits: sideLinerKits,
      panelKits: panelKits,
      otherKits
    })
  }, [kits])

  function switchChecked() {
    setIsSandvikMaterial(!isSandvikMaterial)
    setValue(descriptionName, defaultString)
    setValue(vNumberName, defaultString)
    setValue(partNumberName, defaultString)
  }

  //gathers Material data when the VNumber is changed.
  function onVNumberChange(v) {
    const vNumber = v ?? getValues(vNumberFormName)
    if (!vNumber) {
      return
    }
    const material = materials.find((m) => m.MaterialNumber === vNumber.Number)

    let workingDepth = data?.WorkingDepth ?? defaultString
    const customerMaterialMeta = metadata?.[deckRevision.CustomerId]?.meta?.materials ?? {}

    if (vNumber.Number in customerMaterialMeta) {
      workingDepth = customerMaterialMeta[vNumber.Number].workingDepth
    }

    setValue(vNumberName, vNumber.Number)
    setValue(descriptionName, material?.MaterialDescription ?? defaultString)
    setValue(openAreaName, material?.OpenArea ?? defaultString)
    setValue(partNumberName, vNumber.Number)
    // setValue(partNumberName, material?.PartNumber ?? defaultString)
    setValue(weightName, material?.Weight ?? defaultString)
    setValue(failureDepthName, material?.SafetyDepth)
    setValue(depthName, workingDepth)
    setValue(commentsName, material?.Comments ?? '')
    clearErrors(vNumberName)
  }

  //Creat kit
  async function submit(form) {
    if (form[vNumberName]) {
      if ([...otherMaterials, ...sideLinersMaterials, ...panelMaterials].includes(form[vNumberName])) {
        enqueueSnackbar({
          message: `Material ${getValues('Material Number').Number} already exists in B.O.M`,
          autoHideDuration: 1000,
          variant: 'info'
        })
        setTimeout(() => {
          reset() // Reset the form
        }, 1200)
        return
      }
      // Additional validation for widthName
      if (
        !form[widthName] ||
        isNaN(Number(form[widthName])) ||
        Number(form[widthName]) < 1 ||
        Number(form[widthName]) > 10 ||
        !Number.isInteger(Number(form[widthName]))
      ) {
        setError(widthName, { message: 'Width must be an integer between 1 and 10' })
        return
      }
      if (
        !form[heightName] ||
        isNaN(Number(form[heightName])) ||
        Number(form[heightName]) < 1 ||
        Number(form[heightName]) > 10 ||
        !Number.isInteger(Number(form[heightName]))
      ) {
        setError(heightName, { message: 'Width must be an integer between 1 and 10' })
        return
      }
      const existingColours = deckRevision.Kits.map((k) => k.Colour)
      const freeColours = getColorsForKits(existingColours)

      const kit = new KitsItems({
        MaterialNumber: form[vNumberName],
        MaterialDescription: form[descriptionName],
        PartNumber: form[partNumberName],
        CustomerStockCode: form[siteStockNumber],
        Quantity: Number(form[quantityName]),
        Weight: form[weightName],
        Colour: freeColours[Math.floor(new BigNumber(Math.random()).times(freeColours.length).toNumber())],
        OpenArea: form[openAreaName],
        WorkingDepth: form[depthName],
        FailureWorkingDepth: form[failureDepthName],
        ApertureWidth: form[apertureName],
        FailureApertureWidth: form[failureApertureName],
        Size: new KitSizeItems({
          Height: Number(form[heightName]),
          Width: Number(form[widthName])
        }),
        Comments: form[commentsName]
      })
      const dataClone = Object.assign({}, deckRevision)
      dataClone.Kits.push(kit)

      setDeckRevision(dataClone)

      reset()
      setValue(vNumberName, '')
      setMaterialSelected('')
    } else {
      setError(vNumberName, { message: 'Required' })
      setError(vNumberFormName, { message: 'Required' })
    }
  }

  // Update kit
  async function update(form) {
    const dataClone = Object.assign({}, deckRevision)
    const kit = structuredClone(
      dataClone.Kits?.find((k) => k.MaterialNumber === form[vNumberName]) ?? new KitsItems({})
    )

    const previousWorkingDepth = kit.WorkingDepth

    kit.MaterialNumber = form[vNumberName]
    kit.MaterialDescription = form[descriptionName]
    kit.PartNumber = form[partNumberName]
    kit.CustomerStockCode = form[siteStockNumber]
    kit.Weight = form[weightName]
    kit.Quantity = Number(form[quantityName])
    kit.Colour = form[colourName]
    kit.OpenArea = form[openAreaName]
    kit.WorkingDepth = form[depthName]
    kit.FailureWorkingDepth = form[failureDepthName]
    kit.ApertureWidth = form[apertureName]
    kit.FailureApertureWidth = form[failureApertureName]
    kit.Size = new KitSizeItems({
      Height: Number(form[heightName]),
      Width: Number(form[widthName])
    })
    kit.Comments = form[commentsName]
    dataClone.Kits = dataClone.Kits?.filter((k) => k.MaterialNumber !== kit.MaterialNumber) ?? []
    dataClone.Kits.push(kit)
    setDeckRevision(dataClone)

    if (previousWorkingDepth !== kit.WorkingDepth) {
      addProcess({
        type: 'Kit-Material',
        customerId: dataClone.CustomerId,
        materialNumber: kit.MaterialNumber,
        workingDepth: kit.WorkingDepth
      })
    }

    reset()
    setMaterialSelected('')
    setValue(vNumberName, '')
  }

  //Delete kit
  function remove(modalState) {
    if (modalState?.isSave) {
      const vNumber = getValues(vNumberName)
      if (vNumber) {
        const dataClone = Object.assign({}, deckRevision)
        const kit = dataClone.Kits.splice(
          dataClone.Kits.findIndex((k) => k.MaterialNumber === vNumber),
          1
        )[0]
        if (!kit) {
          return
        }

        const kitPanels = dataClone.Panels.filter((p) => p.MaterialNumber === vNumber)
        const isNewFormat = kitPanels.some((p) => {
          try {
            const state = JSON.parse(p.MaintenanceState || '{}')
            return state.newFormat === true
          } catch {
            return false
          }
        })
        if (isNewFormat) {
          kitPanels.forEach((panel) => {
            const position = panel.Position
            const panel_ = new PanelsItems({
              MaintenanceState: null,
              MaterialNumber: null,
              Position: position
            })
            const position_ = dataClone.Panels.findIndex((p) => {
              return p.Position.Column === position.Column && p.Position.Row === position.Row
            })
            dataClone.Panels.splice(position_, 1, panel_)
          })
        } else {
          kitPanels.forEach((panel) => {
            const position = panel.Position

            const position_ = dataClone.Panels.findIndex((p) => {
              return p.Position.Column === position.Column && p.Position.Row === position.Row
            })
            dataClone.Panels.splice(
              position_,
              1,
              new PanelsItems({
                MaintenanceState: null,
                MaterialNumber: null,
                Position: position
              })
            )

            for (let i = 0; i < kit.Size.Width; i++) {
              for (let j = 0; j < kit.Size.Height; j++) {
                const pos = {
                  Column: position.Column + i,
                  Row: position.Row + j
                }
                const index = dataClone.Panels.findIndex((p) => {
                  return p.Position.Column === pos.Column && p.Position.Row === pos.Row
                })
                const panel_ = new PanelsItems({
                  MaintenanceState: null,
                  MaterialNumber: null,
                  Position: pos
                })

                if (index !== -1) {
                  dataClone.Panels.splice(index, 1, panel_)
                } else {
                  dataClone.Panels.push(panel_)
                }
              }
            }
          })
        }
        dataClone.SideLiners.map((s) => {
          if (s.MaterialNumber === vNumber) {
            s.MaterialNumber = null
          }
        })
        setDeckRevision(dataClone)
      }

      reset()
      setMaterialSelected('')
    }
    setRemoveModalState({ isOpen: false })
  }

  function textInput(name, disableIfData, inputData = {}) {
    return (
      <Grid container spacing={1} alignItems="center">
        <Grid item md={3} sx={inputStyling}>
          <Typography variant="h5" component="h5">
            {name}
          </Typography>
        </Grid>
        <Grid item md={9} sx={inputStyling}>
          <OutlinedInput
            name={name}
            color="secondary"
            fullWidth
            inputProps={inputData}
            disabled={disableIfData && data && Object.entries(data).length > 0}
            {...register(name)}
          />
          {errors[name] && <p>This field requires a correct value</p>}
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      <DeckAccordion title="Insert Deck Kit Item" defaultExpanded onChange={onChange}>
        <Grid container>
          <Stack direction="row" sx={{ width: '100%', alignItems: 'center', justifyContent: 'end' }}>
            <Typography variant="h5" component="h5">
              Sandvik Material
            </Typography>
            <Switch
              checked={isSandvikMaterial}
              disabled={data && Object.entries(data).length > 0}
              onChange={switchChecked}
              color="secondary"
            />
          </Stack>
          {watchVNumber && (
            <Grid
              container
              direction="column"
              gap={2}
              padding="0 1.4em"
              item
              md={3}
              sm={12}
              sx={{ textAlign: 'center', margin: '1em 0 1em 0' }}>
              {watchVNumber ? (
                <Grid item>
                  <LoadingButton
                    variant="contained"
                    color="secondary"
                    size="large"
                    sx={{
                      width: '100%'
                    }}
                    onClick={handleSubmit(data ? update : submit)}>
                    {data ? 'Update' : 'Insert'}
                  </LoadingButton>
                </Grid>
              ) : null}
              {data ? (
                <>
                  <Grid item>
                    <Button
                      variant="contained"
                      color="error"
                      size="large"
                      sx={{
                        width: '100%'
                      }}
                      onClick={() =>
                        setRemoveModalState({
                          isOpen: true,
                          title: 'Remove Item',
                          buttonText: 'Remove',
                          data: { Name: getValues(vNumberName) }
                        })
                      }>
                      Remove item
                    </Button>
                  </Grid>
                </>
              ) : null}
              <Grid item>
                <Button
                  sx={{
                    width: '100%'
                  }}
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setValue(vNumberName, '')
                    setMaterialSelected('')
                    reset()
                  }}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          )}

          <Grid
            container
            item
            md={watchVNumber ? 9 : 12}
            sm={12}
            sx={{
              overflow: 'scroll',
              '&::-webkit-scrollbar': {
                width: '2px',
                height: '2px'
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: theme.palette.supporting.dark
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.supporting.pale,
                borderRadius: 2
              }
            }}>
            <Stack
              minHeight="0"
              minWidth="0"
              overflow="hidden"
              direction="column"
              sx={{
                minHeight: '20px',
                gap: '20px',
                width: '100%',
                minWidth: '300px', // Prevents shrinking below 300px
                flexGrow: 1,
                flexShrink: 0 // Ensures it takes up available space
              }}>
              <Grid container spacing={1} alignItems="center">
                <Grid item md={3} sx={inputStyling}>
                  <Typography variant="h5" component="h5">
                    {isSandvikMaterial ? vNumberFormName : descriptionName}
                  </Typography>
                </Grid>
                <Grid item md={9} sx={inputStyling}>
                  {isSandvikMaterial ? (
                    <>
                      <Controller
                        name={vNumberFormName}
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { value, onChange, ...otherFields } }) => (
                          <Autocomplete
                            fullWidth
                            filterOptions={filterOptions}
                            value={value}
                            disabled={data && Object.entries(data).length > 0}
                            onChange={(e, v) => {
                              onChange(v)
                              onVNumberChange(v)
                            }}
                            options={materials.map((m) => {
                              return {
                                Number: m.MaterialNumber,
                                Render: `${m.MaterialNumber} - ${m.MaterialDescription}`
                              }
                            })}
                            getOptionLabel={(option) => option?.Number ?? ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                              <Typography variant="body2" {...props}>
                                {option.Render}
                              </Typography>
                            )}
                            renderInput={(params) => (
                              <TextField color="secondary" onBlur={() => onVNumberChange()} {...params} />
                            )}
                            sx={{ '& div div': { padding: '3px !important' } }}
                            {...otherFields}
                          />
                        )}
                      />
                    </>
                  ) : (
                    <OutlinedInput
                      id={vNumberName}
                      name={vNumberName}
                      color="secondary"
                      fullWidth
                      required
                      disabled={data && Object.entries(data).length > 0}
                      {...register(vNumberName)}
                    />
                  )}

                  {errors[vNumberName] && <p>This field is required</p>}
                </Grid>
              </Grid>
              {isSandvikMaterial ? textInput(descriptionName, false) : false}
              {!isSandvikMaterial ? textInput(partNumberName, false) : false}
              {/* {textInput(partNumberName, false)} */}
              {isOther ? textInput(quantityName, false) : false}
              {textInput(siteStockNumber, false)}
              {textInput(weightName, false)}
              {textInput(openAreaName, false)}
              {textInput(depthName, false)}
              {textInput(failureDepthName, false)}
              {textInput(apertureName, false)}
              {textInput(failureApertureName, false)}
              {textInput(heightName, true, { type: 'number', min: '1', max: '10', step: '1' })}
              {textInput(widthName, true, { type: 'number', min: '1', max: '10', step: '1' })}
              {textInput(commentsName, false, { maxLength: 120 })}
            </Stack>
          </Grid>
        </Grid>
      </DeckAccordion>

      {removeModalState.isOpen && (
        <FormDialog modalState={removeModalState} onOpenChange={remove}>
          <RemoveForm />
        </FormDialog>
      )}
    </>
  )
}

export default KitItemForm
